import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Scissors, Loader2, LogOut, Sparkles, Send, Mail, Crown, Droplets,
  Bell, Images, Pencil, ChevronRight, Check, X, Plus, Minus,
} from "lucide-react";
import { toast } from "sonner";
import { getUserToken, setUserToken, authHeaders } from "@/utils/auth";
import { getClientKey, getPhone } from "@/utils/access";
import { isTelegramApp, confirmPhoneViaTelegram, subscribeTopInset } from "@/utils/telegram";
import { FaceTexture } from "@/components/FaceTexture";
import {
  EASE, rise, fmtDate, fmtFull, fmtDay, pluralGensNom,
  fieldCls, btnGoldSolid, btnGold, btnGhost,
} from "@/components/accountUi";

import { API } from "../utils/api";

// +7 903 518-42-41 для российских номеров, остальное — как есть
const fmtPhone = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.length === 11 && (d.startsWith("7") || d.startsWith("8"))) {
    return `+7 ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
  }
  return phone || "";
};

const imgUrl = (id) => `${API}/images/${id}`;

// Кольцо прогресса месячной квоты (как в референсе кабинета)
const Ring = ({ value, total }) => {
  const r = 33;
  const c = 2 * Math.PI * r;
  const frac = total > 0 ? Math.min(1, Math.max(0, value / total)) : 0;
  return (
    <div className="relative h-[76px] w-[76px] shrink-0" data-testid="account-quota-ring">
      <svg viewBox="0 0 76 76" className="h-full w-full -rotate-90">
        <circle cx="38" cy="38" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5" />
        <circle
          cx="38" cy="38" r={r} fill="none" stroke="#C2A878" strokeWidth="2.5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - frac)}
          style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-jetbrains text-[13px] text-[#F4F4F0]">
        {value}<span className="text-[#525252]">/{total}</span>
      </span>
    </div>
  );
};

// Построчное появление заголовка, как в hero лендинга
const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function AccountPage() {
  const [token, setToken] = useState(getUserToken());
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(!!getUserToken());
  const [mode, setMode] = useState("telegram");
  const [email, setEmail] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const [topInset, setTopInset] = useState(0);
  const [historyTab, setHistoryTab] = useState("all");
  const [activityOpen, setActivityOpen] = useState(false);
  const [activity, setActivity] = useState(null);
  const [hasNewActivity, setHasNewActivity] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  // Отступ под системный оверлей Telegram fullscreen — иначе шапка прячется под ним
  useEffect(() => subscribeTopInset(setTopInset), []);

  // Лента движений примерок: точка на колокольчике, если пришли новые события
  useEffect(() => {
    if (!me) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/account/activity`, { headers: authHeaders() });
        const events = data.events || [];
        setActivity(events);
        const newest = events[0]?.at;
        setHasNewActivity(Boolean(newest && newest > (localStorage.getItem("activity_seen") || "")));
      } catch { /* лента не критична */ }
    })();
  }, [me]);

  const openActivity = async () => {
    setActivityOpen(true);
    // Всегда подтягиваем свежие события: после примерки лента не должна показывать старый кэш
    try {
      const { data } = await axios.get(`${API}/account/activity`, { headers: authHeaders() });
      const events = data.events || [];
      setActivity(events);
      const newest = events[0]?.at;
      if (newest) localStorage.setItem("activity_seen", newest);
    } catch {
      if (activity === null) setActivity([]);
    }
    setHasNewActivity(false);
  };

  const saveName = async () => {
    if (!nameDraft.trim()) return;
    try {
      await axios.patch(`${API}/account/profile`, { name: nameDraft.trim() }, { headers: authHeaders() });
      setMe({ ...me, name: nameDraft.trim() });
      setEditingName(false);
      toast.success("Имя обновлено");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  // Сохранение фото из истории: в Telegram — нативное скачивание, в браузере — <a download>
  const saveHistoryImage = (h) => {
    // Карточки ухода сохраняются свободно — они уже оплачены квотой; тарифный гейт — только фото примерок
    if (h.kind !== "haircare" && !me?.can_save) {
      toast("Сохранение доступно по тарифу", {
        description: "Оформите тариф — откроется сохранение фото примерок.",
        action: { label: "Понятно", onClick: () => setSelected(null) },
      });
      return;
    }
    const url = imgUrl(h.image_id);
    const tg = window.Telegram?.WebApp;
    // downloadFile — с Bot API 8.0; простая проверка наличия метода ненадёжна (бывают заглушки)
    const canNativeDownload = Boolean(
      tg?.initData && typeof tg.isVersionAtLeast === "function" && tg.isVersionAtLeast("8.0") && tg.downloadFile
    );
    if (canNativeDownload) {
      tg.downloadFile({ url, file_name: `dizayn-pro-${String(h.image_id).slice(0, 6)}.jpg` });
      toast.success("Сохраняем фото — проверьте загрузки");
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = "dizayn-pro.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Фото сохранено");
  };

  // Шеринг примерки. Bot API 8.0+: сервер готовит сообщение, Telegram показывает
  // понятный предпросмотр фото — выбрал чат, и оно отправлено. Старые клиенты —
  // инлайн-режим, браузер — ссылка t.me/share.
  const shareHistoryImage = async (h) => {
    const tg = window.Telegram?.WebApp;
    const canPreparedShare = Boolean(
      tg?.initData && typeof tg.shareMessage === "function" &&
      typeof tg.isVersionAtLeast === "function" && tg.isVersionAtLeast("8.0")
    );
    if (canPreparedShare) {
      try {
        const { data } = await axios.post(
          `${API}/share/prepare`,
          { image_id: h.image_id },
          { headers: authHeaders() }
        );
        tg.shareMessage(data.msg_id, (ok) => {
          if (ok) toast.success("Отправлено");
        });
        return;
      } catch (e) {
        toast.error(
          typeof e.response?.data?.detail === "string"
            ? e.response.data.detail
            : "Не удалось подготовить сообщение. Попробуйте ещё раз."
        );
        return;
      }
    }
    if (tg?.initData && typeof tg.switchInlineQuery === "function") {
      tg.switchInlineQuery(h.image_id, ["users", "groups"]);
      toast.success("Тапните по фото над полем ввода — оно улетит в чат");
      return;
    }
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(imgUrl(h.image_id))}&text=${encodeURIComponent("Моя примерка новой стрижки в «Дизайн Про»")}`,
      "_blank"
    );
  };

  const confirmPhone = async () => {
    try {
      await confirmPhoneViaTelegram();
      const { data } = await axios.get(`${API}/account/me`, { headers: authHeaders() });
      setMe(data);
      toast.success("Номер подтверждён — бесплатные примерки открыты");
    } catch (e) {
      if (e?.message !== "cancelled") {
        toast.error("Не удалось подтвердить номер. Попробуйте ещё раз.");
      }
    }
  };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/account/me`, { headers: authHeaders() })
      .then(({ data }) => setMe(data))
      .catch(() => {
        setUserToken("");
        setToken("");
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Подхватываем аватарку из Telegram-профиля (отдаётся в Mini App initData) и сохраняем в аккаунт
  useEffect(() => {
    if (!me) return;
    let tgPhoto = "";
    try {
      tgPhoto = window.Telegram?.WebApp?.initDataUnsafe?.user?.photo_url || "";
    } catch {}
    if (tgPhoto && tgPhoto !== me.photo_url) {
      axios
        .post(`${API}/account/photo`, { photo_url: tgPhoto }, { headers: authHeaders() })
        .then(() => setMe((m) => (m ? { ...m, photo_url: tgPhoto } : m)))
        .catch(() => {});
    }
  }, [me?.photo_url, !!me]);

  // Возврат со страницы оплаты ЮKassa: опрашиваем статус платежа, пока webhook не активирует тариф
  useEffect(() => {
    const orderId = new URLSearchParams(window.location.search).get("payment");
    if (!orderId || !token) return;
    let stopped = false;
    let tries = 0;
    const cleanUrl = () => window.history.replaceState({}, "", "/account");
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/${orderId}`, { headers: authHeaders() });
        if (stopped) return;
        if (data.subscription_active) {
          toast.success(data.kind === "pack"
            ? `Оплата прошла — примерки на счёте${data.pack_qty ? ` (+${data.pack_qty})` : ""}`
            : "Оплата прошла — тариф активирован");
          cleanUrl();
          const { data: fresh } = await axios.get(`${API}/account/me`, { headers: authHeaders() });
          if (!stopped) setMe(fresh);
          return;
        }
        if (data.status === "canceled" || data.status === "failed") {
          toast.error("Платёж не прошёл — деньги не списаны. Попробуйте ещё раз или другую карту.");
          cleanUrl();
          return;
        }
        if (++tries < 10) setTimeout(check, 3000);
        else {
          toast("Платёж обрабатывается — обновите страницу через минуту");
          cleanUrl();
        }
      } catch {
        if (!stopped && ++tries < 10) setTimeout(check, 3000);
      }
    };
    check();
    return () => { stopped = true; };
  }, [token]);

  const errText = (e) =>
    typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Что-то пошло не так. Попробуйте ещё раз.";

  const startEmail = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/auth/email/start`, { email });
      setChallenge(data.challenge_id);
      toast.success("Код отправлен на почту");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };

  const finishLogin = async (url, body) => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(url, {
        ...body,
        client_key: getClientKey(),
        phone: getPhone(),
      });
      setUserToken(data.token);
      setToken(data.token);
      toast.success("Вы вошли");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };

  const logout = () => {
    setUserToken("");
    setToken("");
    setMe(null);
    setChallenge(null);
    setCode("");
  };

  const sub = me?.subscription;
  const HISTORY_TAB_KINDS = {
    all: null,
    tryon: ["tryon", "look"],
    analysis: ["analysis"],
    haircare: ["haircare"],
  };
  const filteredHistory = (me?.history || []).filter((h) =>
    !HISTORY_TAB_KINDS[historyTab] ? true : HISTORY_TAB_KINDS[historyTab].includes(h.kind)
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F4F0] selection:bg-[#C2A878]/30" data-testid="account-page">
      <FaceTexture />
      <header
        className="fixed inset-x-0 top-0 z-40 border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl"
        style={{ top: topInset }}
      >
        <div className="flex items-center justify-between px-6 py-4 md:px-12">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" data-testid="account-logo">
              <Scissors size={15} strokeWidth={1.5} className="text-[#C2A878]" />
              <span className="font-jetbrains text-sm tracking-[0.35em] text-[#F4F4F0]">AFANASIEV</span>
            </Link>
            <span className="hidden h-4 w-px bg-white/15 sm:block" />
            <span className="hidden font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#525252] sm:block">
              Личный кабинет
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/preview"
              className="flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:text-[#C2A878]"
              data-testid="account-to-app-link"
            >
              В приложение
            </Link>
          </div>
        </div>
      </header>

      <main
        className="relative mx-auto max-w-md overflow-hidden px-6 pb-32 pt-28 md:max-w-lg"
        style={{ paddingTop: 112 + topInset }}
      >
        {/* свечение только внутреннее — в углах карточек (баланс, тариф) */}

        {loading ? (
          <div className="flex justify-center pt-20">
            <Loader2 size={22} className="animate-spin text-[#C2A878]" />
          </div>
        ) : !me ? (
          <div data-testid="account-login">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="font-jetbrains text-[10px] uppercase tracking-[0.35em] text-[#C2A878]"
            >
              ( Вход )
            </motion.p>
            <h1 className="mt-6 font-cormorant font-light leading-[1.02] tracking-tight text-[#F4F4F0]">
              <RevealLine delay={0.1} className="text-[clamp(2.4rem,9vw,3.6rem)]">Личный</RevealLine>
              <RevealLine delay={0.24} className="text-[clamp(2.4rem,9vw,3.6rem)] italic text-[#C2A878]">кабинет</RevealLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
              className="mt-5 text-sm leading-relaxed text-[#A3A3A3]"
            >
              Ваши разборы, примерки и тариф — в одном месте. Без паролей: код из Telegram или с почты.
            </motion.p>

            <motion.div {...rise(1)}>
              <div className="mt-10 flex border border-white/10" data-testid="login-tabs">
                <button
                  onClick={() => setMode("telegram")}
                  className={`flex flex-1 items-center justify-center gap-2 px-4 py-3.5 font-jetbrains text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    mode === "telegram" ? "bg-[#C2A878]/10 text-[#C2A878]" : "text-[#A3A3A3] hover:text-[#F4F4F0]"
                  }`}
                  data-testid="login-tab-telegram"
                >
                  <Send size={13} strokeWidth={1.5} />
                  Telegram
                </button>
                <button
                  onClick={() => setMode("email")}
                  className={`flex flex-1 items-center justify-center gap-2 border-l border-white/10 px-4 py-3.5 font-jetbrains text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    mode === "email" ? "bg-[#C2A878]/10 text-[#C2A878]" : "text-[#A3A3A3] hover:text-[#F4F4F0]"
                  }`}
                  data-testid="login-tab-email"
                >
                  <Mail size={13} strokeWidth={1.5} />
                  Почта
                </button>
              </div>

              {mode === "telegram" ? (
                <div className="mt-8" data-testid="login-telegram">
                  <ol className="space-y-2.5 text-sm leading-relaxed text-[#A3A3A3]">
                    <li><span className="mr-2 font-jetbrains text-[10px] text-[#C2A878]">01</span>Откройте бота @afanasievpro_bot</li>
                    <li><span className="mr-2 font-jetbrains text-[10px] text-[#C2A878]">02</span>Отправьте ему слово «код»</li>
                    <li><span className="mr-2 font-jetbrains text-[10px] text-[#C2A878]">03</span>Введите число из его сообщения</li>
                  </ol>
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    placeholder="Код из бота"
                    className={`${fieldCls} mt-8 text-center font-cormorant text-3xl tracking-[0.4em]`}
                    data-testid="login-telegram-code-input"
                  />
                  <button
                    onClick={() => finishLogin(`${API}/auth/telegram/verify`, { code })}
                    disabled={sending || code.length !== 6}
                    className={`${btnGold} mt-8`}
                    data-testid="login-telegram-submit"
                  >
                    {sending ? <Loader2 size={13} className="animate-spin" /> : null}
                    Войти
                  </button>
                </div>
              ) : (
                <div className="mt-8" data-testid="login-email">
                  {!challenge ? (
                    <>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ваша@почта.ru"
                        className={fieldCls}
                        data-testid="login-email-input"
                      />
                      <button
                        onClick={startEmail}
                        disabled={sending || !email.includes("@")}
                        className={`${btnGold} mt-8`}
                        data-testid="login-email-start-button"
                      >
                        {sending ? <Loader2 size={13} className="animate-spin" /> : null}
                        Получить код
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        inputMode="numeric"
                        placeholder="Код из письма"
                        className={`${fieldCls} text-center font-cormorant text-3xl tracking-[0.4em]`}
                        data-testid="login-email-code-input"
                      />
                      <button
                        onClick={() => finishLogin(`${API}/auth/email/verify`, { challenge_id: challenge, code })}
                        disabled={sending || code.length !== 6}
                        className={`${btnGold} mt-8`}
                        data-testid="login-email-submit"
                      >
                        {sending ? <Loader2 size={13} className="animate-spin" /> : null}
                        Войти
                      </button>
                    </>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        ) : (
          <div data-testid="account-cabinet">
            {/* ——— Профиль: аватар, имя, редактирование ——— */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
              className="mt-2 flex items-center gap-5"
            >
              {me.photo_url && !avatarError ? (
                <img
                  src={me.photo_url}
                  alt={me.name || "Аватар"}
                  onError={() => setAvatarError(true)}
                  className="h-20 w-20 shrink-0 rounded-full border border-[#C2A878]/40 object-cover"
                  data-testid="account-avatar"
                />
              ) : (
                <span
                  className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-[#C2A878]/40 font-jetbrains text-xl uppercase text-[#C2A878]"
                  data-testid="account-avatar"
                >
                  {(me.name || "Г")[0]}
                </span>
              )}
              <div className="min-w-0 flex-1">
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveName()}
                      autoFocus
                      className={`${fieldCls} !py-2`}
                      data-testid="account-name-input"
                    />
                    <button onClick={saveName} aria-label="Сохранить имя" className="shrink-0 text-[#C2A878]" data-testid="account-name-save">
                      <Check size={16} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => setEditingName(false)} aria-label="Отмена" className="shrink-0 text-[#525252]" data-testid="account-name-cancel">
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <h1
                    className="truncate font-cormorant text-3xl font-light italic leading-none text-[#F4F4F0] sm:text-4xl"
                    data-testid="account-name"
                  >
                    {me.name || "Гость"}
                  </h1>
                )}
                <p className="mt-1.5 truncate font-jetbrains text-[10px] tracking-[0.12em] text-[#525252]">
                  {(me.telegram && `@${me.telegram}`) || fmtPhone(me.phone) || me.email}
                </p>
              </div>
              {!editingName && (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={openActivity}
                    className="relative flex h-10 w-10 items-center justify-center border border-white/10 text-[#A3A3A3] transition-colors duration-300 rounded-full hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                    aria-label="Движения по примеркам"
                    data-testid="account-activity-bell"
                  >
                    <Bell size={15} strokeWidth={1.5} />
                    {hasNewActivity && (
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[#C2A878]" data-testid="account-activity-dot" />
                    )}
                  </button>
                  <button
                    onClick={() => { setNameDraft(me.name || ""); setEditingName(true); }}
                    className="flex h-10 w-10 items-center justify-center border border-white/10 text-[#A3A3A3] transition-colors duration-300 rounded-full hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                    aria-label="Редактировать имя"
                    data-testid="account-edit-profile"
                  >
                    <Pencil size={13} strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </motion.div>

            {/* ——— Баланс примерок + квота месяца ——— */}
            <motion.div {...rise(1)} className="relative mt-7 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#C2A878]/[0.06] to-transparent p-6" data-testid="account-balance-card">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full opacity-25"
                style={{ background: "radial-gradient(circle, #C2A878 0%, transparent 70%)" }}
              />
              <div className="flex items-center gap-5">
                <div className="min-w-0 flex-1">
                  <p className="font-cormorant text-5xl font-light leading-none text-[#F4F4F0]" data-testid="account-remaining">
                    {me.remaining}
                  </p>
                  <p className="mt-1 font-jetbrains text-[8px] uppercase leading-[1.6] tracking-[0.2em] text-[#525252]">
                    {pluralGensNom(me.remaining)} осталось
                  </p>
                </div>
                <div className="h-16 w-px shrink-0 bg-white/10" />
                {sub?.active ? (
                  <div className="flex shrink-0 flex-col items-center gap-1.5">
                    <Ring value={sub.gen_remaining} total={sub.gen_per_month} />
                    <p className="text-center font-jetbrains text-[8px] uppercase leading-[1.7] tracking-[0.18em] text-[#525252]">
                      осталось в месяце
                    </p>
                    {sub.next_refill && (
                      <p className="text-center font-jetbrains text-[8px] uppercase tracking-[0.18em] text-[#525252]">
                        обновится <span className="text-[#A3A3A3]">{fmtDay(sub.next_refill)}</span>
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="shrink-0 font-jetbrains text-[9px] uppercase leading-[1.7] tracking-[0.18em] text-[#525252]">
                    из {me.limit}<br />бесплатных
                  </p>
                )}
              </div>
              <Link to="/account/tariffs" className={`${btnGhost} !rounded-full mt-6`} data-testid="account-buy-pack-link">
                Докупить примерки
                <ChevronRight size={13} strokeWidth={1.5} />
              </Link>
            </motion.div>

            {/* ——— Подтверждение номера (Telegram) ——— */}
            {me && !me.phone_verified && isTelegramApp() && (
              <motion.div {...rise(2)} className="mt-6 border border-[#C2A878]/30 bg-[#C2A878]/[0.06] p-6" data-testid="phone-verify-block">
                <p className="text-sm leading-relaxed text-[#C2A878]">
                  Подтвердите номер одним тапом — и получите бесплатную примерку.
                </p>
                <button onClick={confirmPhone} className={`${btnGold} mt-4`} data-testid="phone-verify-button">
                  Подтвердить номер Telegram
                </button>
              </motion.div>
            )}

            {/* ——— Тариф одной строкой: детали и покупка — на странице тарифов ——— */}
            <motion.div {...rise(2)} className="mt-4" data-testid="account-paid-status">
              <Link
                to="/account/tariffs"
                className="group relative flex items-center gap-3.5 overflow-hidden rounded-3xl border border-white/10 px-5 py-4 transition-colors duration-500 hover:border-[#C2A878]/50"
                data-testid="account-plans-link"
              >                {sub?.active && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-20"
                    style={{ background: "radial-gradient(circle, #C2A878 0%, transparent 70%)" }}
                  />
                )}
                <Crown size={15} strokeWidth={1.5} className={sub?.active ? "shrink-0 text-[#C2A878]" : "shrink-0 text-[#525252]"} />
                <span className="min-w-0 flex-1">
                  <span className="block font-jetbrains text-[8px] uppercase tracking-[0.3em] text-[#C2A878]">
                    Ваш тариф
                  </span>
                  <span className="mt-1 block truncate font-cormorant text-xl font-light text-[#F4F4F0]" data-testid="account-master-badge">
                    {sub?.active ? (
                      <>{sub.plan_title} <span className="italic text-[#C2A878]">· {sub.period}</span></>
                    ) : (
                      "Тариф не оформлен"
                    )}
                  </span>
                  <span className="mt-0.5 block font-jetbrains text-[9px] uppercase tracking-[0.18em] text-[#525252]">
                    {sub?.active ? `действует до ${fmtFull(sub.until)}` : "маски бесплатны · примерки по тарифу"}
                  </span>
                </span>
                <span
                  className="flex shrink-0 items-center gap-1.5 font-jetbrains text-[9px] uppercase tracking-[0.18em] text-[#C2A878]"
                  data-testid="account-change-plan"
                >
                  {sub?.active ? "Сменить" : "Выбрать"}
                  <ChevronRight size={13} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </motion.div>

            {/* ——— История: тап открывает фото и детали разбора ——— */}
            <motion.div {...rise(0)} className="mt-14" data-testid="account-history">
              <p className="font-jetbrains text-[10px] uppercase tracking-[0.35em] text-[#C2A878]">( История )</p>
              <h2 className="mt-5 font-cormorant text-4xl font-light leading-[1.05] tracking-tight text-[#F4F4F0]">
                Ваши разборы и <span className="italic text-[#C2A878]">примерки</span>
              </h2>
              <div className="mt-6 flex rounded-full border border-white/10 p-1" data-testid="account-history-tabs">
                {[["all", "Все"], ["tryon", "Примерки"], ["analysis", "Разборы"], ["haircare", "Уходы"]].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setHistoryTab(key)}
                    className={`flex-1 rounded-full px-3 py-2.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${
                      historyTab === key
                        ? "bg-gradient-to-br from-[#D4BA8A] to-[#C2A878] text-[#0A0A0A]"
                        : "text-[#525252] hover:text-[#F4F4F0]"
                    }`}
                    data-testid={`history-tab-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {me.history.length === 0 ? (
                <p className="mt-6 font-cormorant text-2xl font-light italic text-[#525252]">
                  Пока пусто — первая примерка появится здесь.
                </p>
              ) : filteredHistory.length === 0 ? (
                <p className="mt-6 font-cormorant text-2xl font-light italic text-[#525252]">
                  В этой категории пока пусто.
                </p>
              ) : (
                <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {filteredHistory.map((h, i) => {
                    // Фон карточки: для ухода — исходное фото (photo_id), иначе картинка результата
                    const bg = h.photo_id || h.image_id;
                    return (
                    <button
                      key={i}
                      onClick={() => setSelected(h)}
                      className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10 text-left transition-colors duration-500 hover:border-[#C2A878]/60"
                      data-testid={`account-history-item-${i}`}
                    >
                      {bg ? (
                        <img
                          src={imgUrl(bg)}
                          alt={h.rec || "Фото разбора"}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-[#C2A878]/[0.05]">
                          {h.kind === "tryon" ? (
                            <Scissors size={20} strokeWidth={1.2} className="text-[#C2A878]" />
                          ) : h.kind === "haircare" ? (
                            <Droplets size={20} strokeWidth={1.2} className="text-[#C2A878]" />
                          ) : (
                            <>
                              <span className="font-cormorant text-5xl font-light leading-none text-[#C2A878]">
                                {h.score}
                              </span>
                              <span className="font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#525252]">
                                / 100
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      {(h.kind === "tryon" || h.kind === "look" || (h.kind === "analysis" && bg)) && typeof h.score === "number" && h.score > 0 && (
                        <span
                          className="absolute right-2 top-2 border border-[#C2A878]/50 bg-[#0A0A0A]/80 px-2 py-0.5 font-jetbrains text-[9px] text-[#C2A878]"
                          data-testid={`history-score-${i}`}
                        >
                          {h.score}/100
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0A0A]/95 to-transparent p-3 pt-8">
                        <p className="font-jetbrains text-[8px] uppercase tracking-[0.25em] text-[#C2A878]">
                          {h.kind === "tryon"
                            ? "Примерка"
                            : h.kind === "look"
                              ? "Полный образ"
                              : h.kind === "haircare"
                                ? "Подбор ухода"
                                : "Разбор"}
                        </p>
                        <p className="mt-0.5 line-clamp-1 text-[11px] text-[#A3A3A3]">
                          {h.kind === "tryon" || h.kind === "look" ? h.rec : h.kind === "haircare" ? h.hair_type : h.face_shape}
                        </p>
                        <p className="mt-0.5 font-jetbrains text-[9px] text-[#525252]">{fmtDate(h.at)}</p>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </motion.div>

            <button
              onClick={logout}
              className="mt-16 flex items-center gap-2 font-jetbrains text-[10px] uppercase tracking-[0.25em] text-[#525252] transition-colors duration-300 hover:text-[#C2A878]"
              data-testid="account-logout-button"
            >
              <LogOut size={12} strokeWidth={1.5} />
              Выйти
            </button>
          </div>
        )}
      </main>

      {/* ——— Просмотр записи истории ——— */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0A0A]/90 p-5 backdrop-blur-md"
            onClick={() => setSelected(null)}
            data-testid="history-modal"
          >
            <motion.div
              initial={{ y: 32, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 32, opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="flex max-h-[85dvh] w-full max-w-md flex-col border border-white/10 bg-[#0A0A0A]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="overflow-y-auto p-6 pb-4">
                {selected.kind === "tryon" || selected.kind === "look" ? (
                  <>
                    {selected.image_id && (
                      <img
                        src={imgUrl(selected.image_id)}
                        alt={selected.rec || "Примерка"}
                        className="max-h-[46dvh] w-full border border-white/10 object-contain"
                        data-testid="history-modal-image"
                      />
                    )}
                    <p className="mt-5 font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#C2A878]">
                      {selected.kind === "look" ? "Полный образ" : "Примерка"} · {fmtDate(selected.at)}
                    </p>
                    <p className="mt-2 font-cormorant text-2xl font-light leading-snug text-[#F4F4F0]">
                      {selected.rec}
                    </p>
                    {(selected.score > 0 || selected.face_shape) && (
                      <p className="mt-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#C2A878]" data-testid="history-modal-tryon-score">
                        {selected.score > 0 ? `Балл разбора ${selected.score}/100` : ""}
                        {selected.score > 0 && selected.face_shape ? " · " : ""}
                        {selected.face_shape || ""}
                      </p>
                    )}
                  </>
                ) : selected.kind === "haircare" ? (
                  <>
                    {selected.image_id && (
                      <img
                        src={imgUrl(selected.image_id)}
                        alt="Карточка подбора ухода"
                        className="max-h-[46dvh] w-full border border-white/10 object-contain"
                        data-testid="history-modal-image"
                      />
                    )}
                    <p className="mt-5 font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#C2A878]">
                      Подбор ухода · {fmtDate(selected.at)}
                    </p>
                    <p
                      className="mt-3 font-cormorant text-3xl font-light leading-snug text-[#F4F4F0]"
                      data-testid="history-modal-hair-type"
                    >
                      {selected.hair_type}
                    </p>
                    {selected.condition && (
                      <p className="mt-3 inline-block border border-[#C2A878]/40 bg-[#C2A878]/10 px-3 py-1.5 font-jetbrains text-[10px] uppercase tracking-[0.15em] text-[#C2A878]">
                        {selected.condition}
                      </p>
                    )}
                    {selected.summary && (
                      <p className="mt-4 text-sm leading-relaxed text-[#A3A3A3]">{selected.summary}</p>
                    )}
                    {Array.isArray(selected.products) && selected.products.length > 0 && (
                      <ul className="mt-5 space-y-4 border-t border-white/5 pt-4" data-testid="history-modal-products">
                        {selected.products.map((p, j) => (
                          <li key={j}>
                            <p className="font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#C2A878]">
                              {p.category}
                            </p>
                            {p.advice && (
                              <p className="mt-1 text-[13px] leading-relaxed text-[#A3A3A3]">{p.advice}</p>
                            )}
                            {p.examples && (
                              <p className="mt-1 text-xs leading-relaxed text-[#525252]">{p.examples}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#C2A878]">
                      Разбор · {fmtDate(selected.at)}
                    </p>
                    <div className="mt-4 flex items-end gap-3">
                      <p className="font-cormorant text-7xl font-light leading-none text-[#C2A878]" data-testid="history-modal-score">
                        {selected.score}
                      </p>
                      <p className="pb-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
                        / 100 · {selected.face_shape}
                      </p>
                    </div>
                    {selected.summary && (
                      <p className="mt-4 text-sm leading-relaxed text-[#A3A3A3]">{selected.summary}</p>
                    )}
                    {selected.current_style && (
                      <div className="mt-4 border-t border-white/5 pt-4">
                        <p className="font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#C2A878]">
                          ( Ваша текущая стрижка )
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-[#A3A3A3]" data-testid="history-modal-current-style">
                          {selected.current_style}
                        </p>
                      </div>
                    )}
                    {Array.isArray(selected.recs) && selected.recs.length > 0 && (
                      <ul className="mt-5 space-y-2.5 border-t border-white/5 pt-4" data-testid="history-modal-recs">
                        {selected.recs.map((r, j) => (
                          <li key={j} className="flex items-center justify-between gap-3 text-[13px] text-[#A3A3A3]">
                            <span>{r.name}</span>
                            {typeof r.match === "number" && (
                              <span className="font-jetbrains text-[10px] text-[#C2A878]">{r.match}%</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
              <div className="shrink-0 space-y-3 border-t border-white/10 p-4">
                {(selected.kind === "tryon" || selected.kind === "look" || selected.kind === "haircare") && selected.image_id && (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => saveHistoryImage(selected)}
                      className={btnGold}
                      data-testid="history-modal-save"
                    >
                      Сохранить
                    </button>
                    <button
                      onClick={() => shareHistoryImage(selected)}
                      className="flex w-full items-center justify-center gap-2 border border-[#C2A878] bg-[#C2A878] px-6 py-3.5 font-jetbrains text-[11px] uppercase tracking-[0.2em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#D4BA8A] hover:border-[#D4BA8A]"
                      data-testid="history-modal-share"
                    >
                      <Send size={13} strokeWidth={1.5} />
                      Поделиться
                    </button>
                  </div>
                )}
                <button onClick={() => setSelected(null)} className={btnGhost} data-testid="history-modal-close">
                  Закрыть
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ——— Лента движений по примеркам (колокольчик) ——— */}
      <AnimatePresence>
        {activityOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setActivityOpen(false)}
              className="fixed inset-0 z-[60] bg-[#0A0A0A]/70 backdrop-blur-sm"
              data-testid="activity-backdrop"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.5, ease: EASE }}
              className="fixed inset-x-0 bottom-0 z-[65] mx-auto max-h-[70dvh] w-full max-w-md overflow-hidden border-t border-[#C2A878]/20 bg-[#0F0F0F]"
              data-testid="activity-sheet"
            >
              <div className="flex items-start justify-between px-6 pt-5">
                <div>
                  <p className="font-jetbrains text-[10px] uppercase tracking-[0.35em] text-[#C2A878]">( Движения )</p>
                  <p className="mt-2 font-cormorant text-3xl font-light text-[#F4F4F0]">
                    Начисления и <span className="italic text-[#C2A878]">списания</span>
                  </p>
                </div>
                <button
                  onClick={() => setActivityOpen(false)}
                  aria-label="Закрыть ленту"
                  className="mt-1 text-[#525252] transition-colors duration-300 hover:text-[#C2A878]"
                  data-testid="activity-close"
                >
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
              <div className="mt-5 max-h-[48dvh] overflow-y-auto border-t border-white/10 px-6 pb-8">
                {activity === null ? (
                  <div className="flex justify-center py-10">
                    <Loader2 size={18} className="animate-spin text-[#C2A878]" />
                  </div>
                ) : activity.length === 0 ? (
                  <p className="py-10 text-center font-cormorant text-2xl font-light italic text-[#525252]">
                    Пока пусто — движения появятся после первой примерки.
                  </p>
                ) : (
                  activity.map((e, i) => (
                    <div key={i} className="flex items-center gap-4 border-b border-white/5 py-4" data-testid={`activity-event-${i}`}>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center border ${
                          e.type === "credit"
                            ? "border-[#C2A878]/50 text-[#C2A878]"
                            : e.type === "debit"
                              ? "border-white/10 text-[#A3A3A3]"
                              : "border-white/10 text-[#525252]"
                        }`}
                      >
                        {e.type === "credit" ? <Plus size={13} strokeWidth={1.5} /> : e.type === "debit" ? <Minus size={13} strokeWidth={1.5} /> : <Sparkles size={13} strokeWidth={1.5} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm ${e.type === "credit" ? "text-[#C2A878]" : "text-[#F4F4F0]"}`}>{e.title}</span>
                        {e.note && (
                          <span className="mt-0.5 block truncate font-jetbrains text-[10px] text-[#525252]">{e.note}</span>
                        )}
                      </span>
                      <span className="shrink-0 font-jetbrains text-[9px] text-[#525252]">{fmtDate(e.at)}</span>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
